import React, { useEffect, useState } from 'react';
import {
  Button, Descriptions, Drawer, Form, InputNumber, message, Space, Spin, Switch, Tag, Typography,
} from 'antd';
import { EditOutlined } from '@ant-design/icons';
import useAuthStore from '../store/authStore';
import {
  StockItem, getStockItem, updateStockItem, LEGAL_ENTITY_LABELS,
} from '../api/stock';

const { Text } = Typography;

interface Props {
  open: boolean;
  onClose: () => void;
  itemId: number | null;
  width?: number;
  extra?: React.ReactNode;
  refreshKey?: number;
  onUpdated?: () => void;
}

const StockItemInfoDrawer: React.FC<Props> = ({
  open, onClose, itemId, width = 480, extra, refreshKey, onUpdated,
}) => {
  const { hasPermission } = useAuthStore();
  const canWrite = hasPermission('orders:write');
  const [data, setData] = useState<StockItem | null>(null);
  const [loading, setLoading] = useState(false);

  // Edit mode
  const [editMode, setEditMode] = useState(false);
  const [editStock, setEditStock] = useState<number>(0);
  const [editMarkup, setEditMarkup] = useState<number | null>(null);
  const [useCustomMarkup, setUseCustomMarkup] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !itemId) return;
    setLoading(true);
    setData(null);
    setEditMode(false);
    getStockItem(itemId).then(d => {
      setData(d);
      setEditStock(Number(d.current_stock));
      setUseCustomMarkup(d.markup_pct != null);
      setEditMarkup(d.markup_pct != null ? Number(d.markup_pct) : null);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [open, itemId, refreshKey]);

  const save = async () => {
    if (!itemId) return;
    setSaving(true);
    try {
      await updateStockItem(itemId, {
        current_stock: editStock,
        markup_pct: useCustomMarkup ? (editMarkup ?? null) : null,
      });
      const fresh = await getStockItem(itemId);
      setData(fresh);
      setEditMode(false);
      message.success('Сохранено');
      onUpdated?.();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      title={data?.name || 'Позиция склада'}
      open={open}
      onClose={onClose}
      width={width}
      extra={extra || (canWrite && !editMode && data ? (
        <Button icon={<EditOutlined />} onClick={() => setEditMode(true)}>Редактировать</Button>
      ) : null)}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : data && (
        <>
          <Space wrap style={{ marginBottom: 16 }}>
            {data.is_active ? <Tag color="green">Активна</Tag> : <Tag>Архив</Tag>}
            <Tag color={data.legal_entity === 'autoregium' ? 'blue' : 'purple'}>
              {LEGAL_ENTITY_LABELS[data.legal_entity]}
            </Tag>
            {data.is_marked && <Tag color="gold">ЧЗ / маркировка</Tag>}
          </Space>

          {!editMode && (
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Наименование">{data.name}</Descriptions.Item>
              <Descriptions.Item label="Юр. лицо">{data.legal_entity_label}</Descriptions.Item>
              <Descriptions.Item label="Остаток">
                {Number(data.current_stock).toLocaleString('ru-RU')} {data.unit}
              </Descriptions.Item>
              <Descriptions.Item label="Себестоимость">
                {data.last_receipt_price != null
                  ? `${Number(data.last_receipt_price).toLocaleString('ru-RU')} ₽`
                  : <Text type="secondary">нет прихода</Text>}
              </Descriptions.Item>
              <Descriptions.Item label="Наценка">
                {data.effective_markup_pct != null ? (
                  <Space>
                    <span>{Number(data.effective_markup_pct).toLocaleString('ru-RU')}%</span>
                    {data.markup_pct == null && (
                      <Text type="secondary" style={{ fontSize: 11 }}>(по умолчанию)</Text>
                    )}
                  </Space>
                ) : <Text type="secondary">—</Text>}
              </Descriptions.Item>
              <Descriptions.Item label="Цена продажи">
                {data.selling_price != null
                  ? <b>{Number(data.selling_price).toLocaleString('ru-RU')} ₽</b>
                  : <Text type="secondary">—</Text>}
              </Descriptions.Item>
              {data.notes && <Descriptions.Item label="Примечание">{data.notes}</Descriptions.Item>}
            </Descriptions>
          )}

          {editMode && (
            <Form layout="vertical">
              <Form.Item label="Остаток">
                <InputNumber
                  min={0} step={1} value={editStock}
                  onChange={(v) => setEditStock(Number(v) || 0)}
                  style={{ width: 180 }}
                  addonAfter={data.unit}
                />
              </Form.Item>
              <Form.Item label="Персональная наценка">
                <Space>
                  <Switch
                    checked={useCustomMarkup}
                    onChange={(v) => setUseCustomMarkup(v)}
                  />
                  <span>{useCustomMarkup ? 'Задать вручную' : 'По умолчанию (настройки)'}</span>
                </Space>
                {useCustomMarkup && (
                  <div style={{ marginTop: 8 }}>
                    <InputNumber
                      min={0} max={1000} step={1} precision={2}
                      value={editMarkup ?? undefined}
                      onChange={(v) => setEditMarkup(v == null ? null : Number(v))}
                      style={{ width: 180 }}
                      addonAfter="%"
                    />
                  </div>
                )}
              </Form.Item>
              <Space>
                <Button type="primary" onClick={save} loading={saving}>Сохранить</Button>
                <Button onClick={() => setEditMode(false)}>Отмена</Button>
              </Space>
            </Form>
          )}
        </>
      )}
    </Drawer>
  );
};

export default StockItemInfoDrawer;
